import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Download, Calendar, Users, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useApp } from "@/context/AppContext";
import { listBookings, requestBookingsLink, voucherUrl } from "@/lib/api";
import { euro } from "@/lib/format";

const TX = {
  over: { it: "Le mie prenotazioni", en: "My bookings", hr: "Moje rezervacije", sl: "Moje rezervacije", de: "Meine Buchungen", hu: "Foglalásaim", cs: "Moje rezervace" },
  title: { it: "Ritrova le tue prenotazioni", en: "Find your bookings", hr: "Pronađi svoje rezervacije", sl: "Poišči svoje rezervacije", de: "Deine Buchungen finden", hu: "Foglalásaid megkeresése", cs: "Najdi své rezervace" },
  sub: { it: "Inserisci l'email usata per prenotare: ti invieremo un link sicuro.", en: "Enter the email you booked with: we'll send you a secure link.", hr: "Unesi email s kojim si rezervirao: poslat ćemo siguran link.", sl: "Vnesi e-poštni naslov, s katerim si rezerviral: poslali ti bomo varno povezavo.", de: "Gib die E-Mail an, mit der du gebucht hast: Wir senden dir einen sicheren Link.", hu: "Add meg a foglaláskor használt e-mailt: biztonságos linket küldünk.", cs: "Zadej e-mail, se kterým jsi rezervoval: pošleme ti bezpečný odkaz." },
  find: { it: "Invia link sicuro", en: "Send secure link", hr: "Pošalji link", sl: "Pošlji varno povezavo", de: "Sicheren Link senden", hu: "Biztonságos link küldése", cs: "Poslat bezpečný odkaz" },
  sent_title: { it: "Controlla la tua email", en: "Check your email", hr: "Provjeri email", sl: "Preveri svojo e-pošto", de: "Überprüfe deine E-Mail", hu: "Ellenőrizd az e-mailed", cs: "Zkontroluj svůj e-mail" },
  sent_sub: { it: "Se esistono prenotazioni per questa email, ti abbiamo inviato un link sicuro (valido 7 giorni).", en: "If bookings exist for this email, we've sent you a secure link (valid 7 days).", hr: "Ako postoje rezervacije, poslali smo siguran link (7 dana).", sl: "Če za ta e-poštni naslov obstajajo rezervacije, smo ti poslali varno povezavo (velja 7 dni).", de: "Falls Buchungen für diese E-Mail existieren, haben wir dir einen sicheren Link geschickt (7 Tage gültig).", hu: "Ha vannak foglalások ehhez az e-mailhez, biztonságos linket küldtünk (7 napig érvényes).", cs: "Pokud pro tento e-mail existují rezervace, poslali jsme ti bezpečný odkaz (platný 7 dní)." },
  invalid: { it: "Link non valido o scaduto. Richiedine uno nuovo.", en: "Invalid or expired link. Request a new one.", hr: "Nevažeći ili istekao link.", sl: "Neveljavna ali potekla povezava. Zahtevaj novo.", de: "Ungültiger oder abgelaufener Link. Fordere einen neuen an.", hu: "Érvénytelen vagy lejárt link. Kérj újat.", cs: "Neplatný nebo vypršelý odkaz. Vyžádej si nový." },
  empty: { it: "Nessuna prenotazione trovata.", en: "No bookings found.", hr: "Nema rezervacija.", sl: "Ni najdenih rezervacij.", de: "Keine Buchungen gefunden.", hu: "Nincs foglalás.", cs: "Žádné rezervace." },
  ref: { it: "Riferimento", en: "Reference", hr: "Referenca", sl: "Sklic", de: "Referenz", hu: "Hivatkozás", cs: "Reference" },
  paid: { it: "Pagato", en: "Paid", hr: "Plaćeno", sl: "Plačano", de: "Bezahlt", hu: "Fizetve", cs: "Zaplaceno" },
  confirmed: { it: "Confermato", en: "Confirmed", hr: "Potvrđeno", sl: "Potrjeno", de: "Bestätigt", hu: "Megerősítve", cs: "Potvrzeno" },
  voucher: { it: "Scarica voucher PDF", en: "Download voucher PDF", hr: "Preuzmi voucher PDF", sl: "Prenesi voucher PDF", de: "Voucher-PDF herunterladen", hu: "Voucher PDF letöltése", cs: "Stáhnout voucher PDF" },
};

export default function Bookings() {
  const { lang } = useApp();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const c = (k) => TX[k][lang] ?? TX[k].en;

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    listBookings(token)
      .then(setItems)
      .catch(() => {
        setItems([]);
        toast.error(c("invalid"));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const request = async (e) => {
    e?.preventDefault();
    if (!email) {
      toast.error(c("sub"));
      return;
    }
    try {
      await requestBookingsLink(email, lang);
      setSent(true);
    } catch {
      toast.error("Error");
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-sand">
      <Navbar solid />
      <div className="mx-auto max-w-3xl px-5 pb-24 pt-28 lg:px-8">
        <p className="overline text-terracotta">{c("over")}</p>
        <h1 className="mt-1 font-serif text-4xl font-medium text-petrol lg:text-5xl">{c("title")}</h1>

        {!token && (
          sent ? (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} data-testid="link-sent"
              className="mt-8 flex flex-col items-center rounded-3xl border border-sand-border bg-sand-card py-16 text-center">
              <MailCheck size={40} className="text-terracotta" />
              <h2 className="mt-4 font-serif text-2xl text-petrol">{c("sent_title")}</h2>
              <p className="mt-2 max-w-sm text-petrol/60">{c("sent_sub")}</p>
            </motion.div>
          ) : (
            <>
              <p className="mt-3 text-petrol/70">{c("sub")}</p>
              <form onSubmit={request} className="mt-6 flex gap-3">
                <input type="email" data-testid="bookings-email-input" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
                  className="flex-1 rounded-none border border-sand-border bg-white px-5 py-3 text-sm outline-none focus:border-terracotta" />
                <button type="submit" data-testid="bookings-request-btn"
                  className="flex items-center gap-2 rounded-none bg-terracotta px-6 py-3 font-semibold text-white transition-colors hover:bg-terracotta-hover">
                  <Search size={16} /> {c("find")}
                </button>
              </form>
            </>
          )
        )}

        <div className="mt-8 flex flex-col gap-3">
          {loading && <div className="h-28 animate-pulse rounded-2xl bg-sand-card" />}
          {items && items.length === 0 && !loading && (
            <p className="rounded-2xl border border-dashed border-sand-border bg-sand-card py-12 text-center text-petrol/60">{c("empty")}</p>
          )}
          {items?.map((b, i) => (
            <motion.div key={b.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              data-testid="booking-row" className="flex flex-col gap-4 rounded-2xl border border-sand-border bg-sand-card p-4 sm:flex-row sm:items-center">
              <img src={b.image} alt={b.name} className="h-24 w-full rounded-xl object-cover sm:w-32" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Link to={`/stay/${b.slug}`} className="font-serif text-lg font-semibold text-petrol hover:text-terracotta">{b.name}</Link>
                  <span className={`rounded-none px-2 py-0.5 text-[0.65rem] font-bold uppercase ${b.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-gold/30 text-petrol"}`}>
                    {b.status === "paid" ? c("paid") : c("confirmed")}
                  </span>
                </div>
                <p className="text-xs text-petrol/50">{b.zone_name}</p>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-petrol/70">
                  <span className="flex items-center gap-1.5"><Calendar size={13} /> {b.check_in || "-"} → {b.check_out || "-"}</span>
                  <span className="flex items-center gap-1.5"><Users size={13} /> {b.guests}</span>
                  <span className="font-serif text-lg font-bold text-petrol">{euro(b.price)}</span>
                </div>
                <p className="mt-1 font-mono text-xs text-petrol/40">{c("ref")}: {b.id}</p>
              </div>
              <a href={voucherUrl(b.id)} target="_blank" rel="noreferrer" data-testid={`voucher-download-${b.id}`}
                className="flex items-center justify-center gap-2 rounded-none bg-petrol px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-terracotta">
                <Download size={15} /> {c("voucher")}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

